import { HttpClient } from '@angular/common/http';
import { inject, InjectionToken, Provider } from '@angular/core';
import { LaravelResult } from '@fsf/util';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  layerToAsyncState,
  MappedSiteInsights,
  siteInsightsBundleSchema,
} from '../models/site-insights.model';

/**
 * The candidate-sites resource base; the bundle lives at `${base}/${siteId}/insights`. Behind
 * a token so `@fsf/discover` takes no dependency on the app's `environment`.
 */
export const SITE_INSIGHTS_ENDPOINT = new InjectionToken<string>(
  'site-insights.endpoint.url',
);

export interface SiteInsightsApiConfig {
  http: HttpClient;
  endpointUrl: string;
}

/**
 * Parses the four-layer bundle at the boundary and maps each backend layer status to its
 * `AsyncState`, so drift errors the call rather than corrupting an overlay. A transport failure
 * errors the stream, which the store folds into its single page-level surface.
 *
 * Not `@Injectable` — only ever built via `new`, so AOT must not analyze its config-object
 * constructor parameter as a DI token.
 */
export class SiteInsightsApiRepository {
  readonly #http: HttpClient;
  readonly #endpointUrl: string;

  constructor(config: SiteInsightsApiConfig) {
    this.#http = config.http;
    this.#endpointUrl = config.endpointUrl;
  }

  loadForSite(siteId: string): Observable<MappedSiteInsights> {
    return this.#http
      .get<LaravelResult<unknown>>(`${this.#endpointUrl}/${siteId}/insights`)
      .pipe(
        map((result) => {
          const bundle = siteInsightsBundleSchema.parse(result.data);
          return {
            isochrones: layerToAsyncState(bundle.isochrones),
            competition: layerToAsyncState(bundle.competition),
            parking: layerToAsyncState(bundle.parking),
            zoning: layerToAsyncState(bundle.zoning),
            computedAt: {
              isochrones: bundle.isochrones.computedAt,
              competition: bundle.competition.computedAt,
              parking: bundle.parking.computedAt,
              zoning: bundle.zoning.computedAt,
            },
          } satisfies MappedSiteInsights;
        }),
      );
  }
}

/** Bound as its own token: the store injects the concrete class. */
export function provideSiteInsightsApi(): Provider {
  return {
    provide: SiteInsightsApiRepository,
    useFactory: () =>
      new SiteInsightsApiRepository({
        http: inject(HttpClient),
        endpointUrl: inject(SITE_INSIGHTS_ENDPOINT),
      }),
  };
}
